import {
  Box,
  Grid,
  IconButton,
  ListItemButton,
  alpha,
  lighten,
  useTheme
} from '@mui/material'
import { Reorder } from 'framer-motion'
import {
  DocType,
  EventDoc,
  MasterLabelDoc,
  MasterLabelLabel,
  UserSettingsDoc
} from 'types/api/changes'
import { Model } from 'types/model'
import { convertLabelColorToStringColor } from 'utils/masterLabel'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import './styles.scss'
import { t } from 'i18next'
import { AppUIType, useAppUIType } from 'components/providers/AppUITypeProvider'

import { withObservables } from '@nozbe/watermelondb/react'
import ObserveService from 'services/db/Observe'
import { compose } from 'recompose'
import { ComponentType, useEffect, useState } from 'react'
import AddLabelPopup from 'components/popups/AddLabelPopup'
import LabelLoader from 'components/loaders/LabelLoader'
import { LabelIcon } from 'assets/icons'
import { labelText } from 'utils/label'
import i18n from 'i18n'
import Logger from 'services/Logger'
import { isTrulyEmpty } from 'utils/object'
import dayjs from 'dayjs'
import { useRecurrenceWorker } from 'hooks/useRecurrenceWorker'
import useLocalStorage from 'components/LocalStorageHook'
import { LocalStorageKey } from 'types/services/localStorage'
import { APP_COLOR } from 'utils/colors'

interface LabelsListProps {
  userSettings: Model<UserSettingsDoc>
  events: Model<EventDoc>[]
  masterLabel: Model<MasterLabelDoc>

  handleCloseContextMenu: () => void
  handleContextMenu: (
    event: React.MouseEvent<HTMLElement>,
    label: MasterLabelLabel
  ) => void
}

function LabelsList(props: LabelsListProps) {
  const tfmContext = useAppUIType()
  const showingDocType =
    tfmContext.uiType.value === AppUIType.tasks ? DocType.TASK : DocType.NOTE
  const appUiTypeContext = useAppUIType()
  const [someDayDate, setSomedayDate] = useLocalStorage(
    LocalStorageKey.SOMEDAY_DATE,
    dayjs().tz('UTC').startOf('day').unix().toString()
  )
  // Use the custom hook
  const { processedEvents, setProcessedEvents } = useRecurrenceWorker({
    events: props.events,
    selectedLabel: appUiTypeContext.selectedLabel,
    showingDocType,
    someDayDate
  })

  // keep only unique events in array
  const uniqueProcessedEvents = Array.from(
    new Map(processedEvents.map((event) => [event._id, event])).values()
  )

  return (
    <>
      {props.masterLabel?.Labels.map((row) => (
        <Reorder.Item as="div" key={row.ID} value={row} dragListener={false}>
          <LabelRow
            label={row}
            itemsAmount={
              uniqueProcessedEvents?.filter((event) => {
                if (event.Label && typeof event.Label !== 'string') {
                  return event.Label.some(
                    (ele) =>
                      ele.LabelText === row.ID &&
                      event.Type === showingDocType &&
                      !isTrulyEmpty(ele.LabelText)
                  )
                }
              }).length ?? 0
            }
            handleContextMenu={props.handleContextMenu}
          />
        </Reorder.Item>
      ))}
      {!props.masterLabel && (
        <Box marginTop={3}>
          {[...Array(3)].map((_, i) => (
            <LabelLoader key={i} />
          ))}
        </Box>
      )}
    </>
  )
}

export default compose(
  withObservables([], () => ({
    userSettings: ObserveService.UserSettings()
  })),
  withObservables([], (props: LabelsListProps) => ({
    events: ObserveService.Events(undefined)
  }))
)(LabelsList as ComponentType<unknown>) as any

interface LabelRowProps {
  label: MasterLabelLabel
  itemsAmount: number
  handleContextMenu: (
    event: React.MouseEvent<HTMLElement>,
    label: MasterLabelLabel
  ) => void
}

function LabelRow(props: LabelRowProps) {
  const theme = useTheme()
  const tfmContext = useAppUIType()

  const selectLabel = () => {
    tfmContext.setSelectedLabel(props.label)
  }

  return (
    <AddLabelPopup
      label={props.label}
      edit={tfmContext.labelInfo === props.label.ID}
      open={
        tfmContext.labelInfo === props.label.ID || props.label.ID === 'temp'
      }
      setOpen={(b: boolean) => {
        if (!b) {
          tfmContext.setLabelInfo(undefined)
          tfmContext.setSelectedLabel(undefined)
        }
      }}
    >
      <ListItemButton
        id={`label-button-${props.label.Text}`}
        className="labelRow"
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '71px',
          marginLeft: '5px',
          marginRight: '5px',
          overflow: 'hidden',
          position: 'relative',
          padding: theme.spacing(0, 4),
          textAlign: 'unset !important',
          '&.Mui-selected': {
            backgroundColor: `${
              theme.palette.mode === 'dark'
                ? alpha(APP_COLOR, 0.4)
                : lighten(APP_COLOR, 0.8)
            } !important`
          }
        }}
        onContextMenu={(e) => {
          if (props.handleContextMenu) props.handleContextMenu(e, props.label)
        }}
        onClick={selectLabel}
        selected={tfmContext.selectedLabel?.ID === props.label.ID}
      >
        <Grid
          justifyItems={'center'}
          alignItems={'center'}
          display={'flex'}
          flexDirection={'row'}
          flex={'1 1 0'}
          minWidth={0}
          gap={0}
        >
          <LabelIcon
            color={convertLabelColorToStringColor(props.label.Color)}
            style={{
              height: '21px',
              width: '48px',
              transform: i18n.dir() === 'rtl' ? 'rotate(180deg)' : 'none'
            }}
          />
          <Grid
            flex={'1 1 0'}
            minWidth={0}
            item
            display={'flex'}
            flexDirection={'column'}
            marginLeft={'1rem'}
            marginRight={'1rem'}
          >
            <Box
              display={'block'}
              textAlign={undefined}
              fontSize={'16px'}
              width={'100%'}
              minWidth={0}
              overflow={'hidden'}
              textOverflow={'ellipsis'}
              whiteSpace={'nowrap'}
            >
              {labelText(props.label)}
            </Box>
            <div
              className="textGroup"
              id={`label-items-amount-${props.label.Text}`}
            >
              {t('itemsAmount', {
                itemsAmount: props.itemsAmount,
                itemTitle:
                  tfmContext.uiType.value === AppUIType.tasks
                    ? t('task', { count: props.itemsAmount })
                    : t('note', { count: props.itemsAmount })
              })}
            </div>
          </Grid>
        </Grid>
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            height: '1px',
            width: '85%',
            margin: '0 8px',
            background: theme.palette.border.primary
          }}
        ></div>
        <IconButton
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            if (props.handleContextMenu) props.handleContextMenu(e, props.label)
          }}
          className="hoverButton"
          sx={{ flexShrink: 0 }}
        >
          <MoreVertIcon />
        </IconButton>
      </ListItemButton>
    </AddLabelPopup>
  )
}
